"use client";

import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { SEO } from "@/components/seo/SEO";

const Hero = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });
  const { t } = useLanguage();

  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.9]);
  const y = useTransform(scrollYProgress, [0, 0.5], [0, 100]);

  const handleScrollToGallery = () => {
    const gallerySection = document.getElementById('commercial-gallery');
    if (gallerySection) {
      const offset = 80;
      const elementPosition = gallerySection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <>
      <SEO 
        title="Dumitrescu Photography | Professional Editorial & Architectural Photography"
        description="Award-winning professional photographer specializing in editorial, architectural, portrait, and artistic photography in Sofia, Bulgaria. Photo Art by the Grace of God."
        keywords="professional photographer, editorial photography, architectural photography, portrait photography, Sofia photographer, Bulgaria, fine art photography"
      />
      <section
        id="hero"
        ref={containerRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden theme-transition"
        aria-label={t('hero.title')}
        style={{
          backgroundColor: 'hsl(var(--background))',
        }}
      >
        {/* Background with subtle texture */}
        <div 
          className="absolute inset-0 opacity-90"
          style={{
            background: 'linear-gradient(135deg, hsl(var(--background)) 0%, hsl(var(--card)) 50%, hsl(var(--background)) 100%)',
          }}
          aria-hidden="true"
        />
        
        {/* Decorative grid overlay */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `
              linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px),
              linear-gradient(180deg, hsl(var(--foreground)) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
          aria-hidden="true"
        />

        <motion.div
          style={{ opacity, scale, y }}
          className="relative z-10 text-center px-4 sm:px-6 lg:px-8"
        >
          {/* Main heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="font-inter font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight mb-6 md:mb-8"
            style={{ color: 'hsl(var(--foreground))' }}
          >
            <span className="block">Photo Art, by the Grace of God</span>
            <motion.span
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: "100%" }}
              transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="block h-px bg-primary mx-auto mt-6 md:mt-8 max-w-xs sm:max-w-sm md:max-w-md"
              aria-hidden="true"
            />
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="font-inter font-normal text-base sm:text-lg md:text-xl max-w-md sm:max-w-lg md:max-w-xl mx-auto mb-12 md:mb-16 leading-relaxed"
            style={{ color: 'hsl(var(--foreground) / 0.9)' }}
          >
            Putting His creations (subjects), into Its own Light, through a humble opinion (vision)...
          </motion.p>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col items-center"
          >
            <span 
              className="font-inter font-medium text-sm uppercase tracking-wider mb-2"
              style={{ color: 'hsl(var(--foreground) / 0.8)' }}
            >
              {t('hero.explore')}
            </span>
            <motion.button
              onClick={handleScrollToGallery}
              className="focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background rounded-full p-2"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-label="Scroll to commercial gallery section"
            >
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
              >
                <ChevronDown className="w-6 h-6 sm:w-7 sm:h-7 text-primary min-w-[44px] min-h-[44px]" aria-hidden="true" />
              </motion.div>
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Skip to content link for accessibility */}
        <a
          href="#commercial-gallery"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-primary text-primary-foreground px-4 py-3 font-inter font-bold text-sm rounded-none min-h-[44px]"
        >
          Skip to commercial photography gallery
        </a>
      </section>
    </>
  );
};

export default Hero;